import React, { useState, useEffect } from 'react';
import { Button, TextField, Container, Typography, Box, Grid } from '@mui/material';

const MyBankAccountDetails = ({ authToken }) => {
  const [ibanNumber, setIbanNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [branchCode, setBranchCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditable, setIsEditable] = useState(false);  // Track if form is editable
  const [isExisting, setIsExisting] = useState(false);  // Track if account details already exist

  // Fetch existing account details when the component mounts
  useEffect(() => {
    const fetchAccountDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/me/account-details`, {
        // const response = await fetch('http://localhost:8000/me/account-details', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch account details');
        }

        const data = await response.json();
        if (data) {
          setIbanNumber(data.iban_number);
          setAccountName(data.account_name);
          setBranchCode(data.branch_code);
          setIsExisting(true);  // Data exists, so it's an update
        }
      } catch (err) {
        setError('Failed to fetch account details');
      }
    };

    fetchAccountDetails();
  }, [authToken]);

  // Handle form submission for saving account details
  const handleSubmit = async (event) => {
    event.preventDefault();

    const accountDetails = {
      iban_number: ibanNumber,
      account_name: accountName,
      branch_code: branchCode,
    };

    const method = isExisting ? 'PUT' : 'POST';  // Determine if it's a POST or PUT request
    const url = `${process.env.REACT_APP_API_BASE_URL}/me/account-details`;
    // const url = 'http://localhost:8000/me/account-details';
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(accountDetails),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Something went wrong');
      }

      const data = await response.json();
      setSuccess('Account details saved successfully!');
      setError('');
      setIsEditable(false);  // Disable editing after save
      setIsExisting(true);  // Ensure we know it's an update now
    } catch (err) {
      setError(err.message);
      setSuccess('');
    }
  };

  return (
    <Container maxWidth="sm">
      <Typography variant="h4" align="center" gutterBottom>
        My Bank Account Details
      </Typography>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="IBAN Number"
              variant="outlined"
              fullWidth
              value={ibanNumber}
              onChange={(e) => setIbanNumber(e.target.value)}
              disabled={!isEditable}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Account Name"
              variant="outlined"
              fullWidth
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              disabled={!isEditable}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Branch Code"
              variant="outlined"
              fullWidth
              value={branchCode}
              onChange={(e) => setBranchCode(e.target.value)}
              disabled={!isEditable}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <Box textAlign="center">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!isEditable}
              >
                Save Account Details
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      <Box textAlign="center" mt={2}>
        <Button onClick={() => setIsEditable(true)} disabled={isEditable}>
          Edit
        </Button>
      </Box>

      {error && (
        <Box mt={2} textAlign="center">
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {success && (
        <Box mt={2} textAlign="center">
          <Typography color="success">{success}</Typography>
        </Box>
      )}
    </Container>
  );
};

export default MyBankAccountDetails;
